import type { Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";

export type BlockStyle =
  | "display"
  | "heading"
  | "subheading"
  | "paragraph"
  | "small"
  | "blockquote"
  | "bulletList"
  | "orderedList";


export function blockStyle(editor: Editor, pos: number): BlockStyle | null {
  const node = editor.state.doc.nodeAt(pos);
  if (!node) return null;
  if (node.type.name === "heading")
    return node.attrs.level === 1 ? (node.attrs.size === "display" ? "display" : "heading") : "subheading";
  if (node.type.name === "paragraph" && node.attrs.size === "small") return "small";
  if (["paragraph", "blockquote", "bulletList", "orderedList"].includes(node.type.name))
    return node.type.name as BlockStyle;
  return null;
}

/**
 * Converts the whole top-level block at `pos`, unwrapping quotes and lists
 * first. `focus: false` leaves the keyboard down (the mobile bar).
 */
export function turnInto(
  editor: Editor,
  pos: number,
  style: BlockStyle,
  { focus = true }: { focus?: boolean } = {},
) {
  const node = editor.state.doc.nodeAt(pos);
  const current = blockStyle(editor, pos);
  if (!node || !current || current === style) return;
  // Display and Heading, Small and Body, are sizes of one kind of block:
  // switching between them keeps its colour, alignment and identity.
  const sizes: Partial<Record<BlockStyle, [string, string | null]>> = {
    display: ["heading", "display"],
    heading: ["heading", null],
    small: ["paragraph", "small"],
    paragraph: ["paragraph", null],
  };
  const resize = sizes[style];
  if (resize && sizes[current]?.[0] === resize[0] && node.type.name === resize[0]) {
    editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, size: resize[1] }));
    return;
  }
  const doc = editor.state.doc;
  const from = TextSelection.near(doc.resolve(pos + 1)).from;
  const to = TextSelection.near(doc.resolve(pos + node.nodeSize - 1), -1).to;
  const chain = (focus ? editor.chain().focus() : editor.chain()).setTextSelection({
    from,
    to,
  });
  if (current === "bulletList") chain.toggleBulletList();
  else if (current === "orderedList") chain.toggleOrderedList();
  else if (current === "blockquote") chain.lift("blockquote");
  if (style === "display" || style === "heading" || style === "subheading")
    chain.setHeading({ level: style === "subheading" ? 2 : 1 });
  else chain.setParagraph();
  if (style === "display") chain.updateAttributes("heading", { size: "display" });
  else if (style === "small") chain.updateAttributes("paragraph", { size: "small" });
  if (style === "bulletList") chain.toggleBulletList();
  else if (style === "orderedList") chain.toggleOrderedList();
  else if (style === "blockquote") chain.toggleBlockquote();
  chain
    .command(({ tr }) => {
      tr.setSelection(TextSelection.create(tr.doc, tr.selection.to));
      return true;
    })
    .run();
}

export const styleLabels: Record<BlockStyle, string> = {
  display: "Display",
  heading: "Heading",
  subheading: "Subheading",
  paragraph: "Body",
  small: "Small",
  blockquote: "Quote",
  bulletList: "Bulleted list",
  orderedList: "Numbered list",
};
export const layoutLabels = { grid: "Grid", list: "List", index: "Index" } as const;
export const pageDisplayLabels = { card: "Row", feature: "Feature", link: "Index" } as const;
/** Picture and video styles. */
export const mediaStyleLabels = { standard: "Standard", framed: "Framed", bleed: "Full bleed" } as const;
